import { Component, Injector, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { Color, ScaleType } from '@swimlane/ngx-charts';
import { AbstractComponent, AnalyticsMessagesData, Channel, ChannelService, ChannelTypeEnum, InsightsService, ConversationCategoryEnum, DateUtil, ConversationTypeEnum, ConversationDataPoint } from 'lib-trend-core';
import { debounceTime, takeUntil } from 'rxjs';

const CATEGORY_TRANSLATIONS: Record<string, string> = {
  [ConversationCategoryEnum.AUTHENTICATION]: 'report.category.authentication',
  [ConversationCategoryEnum.MARKETING]: 'report.category.marketing',
  [ConversationCategoryEnum.SERVICE]: 'report.category.service',
  [ConversationCategoryEnum.UTILITY]: 'report.category.utility'
};

const TYPE_TRANSLATIONS: Record<string, string> = {
  [ConversationTypeEnum.FREE_ENTRY]: 'report.conversationType.freeEntry',
  [ConversationTypeEnum.FREE_TIER]: 'report.conversationType.freeTier',
  [ConversationTypeEnum.REGULAR]: 'report.conversationType.regular'
};

interface CardData {
  label: string;
  value: number | string;
  colorClass: string;
}

interface ChartData {
  name: string;
  series: { name: string; value: number | string }[];
}

@Component({
    selector: 'app-report-conversation-analytics',
    templateUrl: './report-conversation-analytics.v2.component.html',
    // templateUrl: './report-conversation-analytics.component.html',
    styleUrl: './report-conversation-analytics.v2.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReportConversationAnalyticsComponent extends AbstractComponent implements OnInit {
  @ViewChild('picker', { static: false }) picker: MatDatepicker<Date>;

  private readonly colorMap: Record<string, string> = {
    'Autenticação': 'border-red-400',
    'Marketing': 'border-cyan-400',
    'Serviço': 'border-green-600',
    'Utilidade': 'border-cyan-600'
  };

  isPicker = false;
  listChannel: Channel[] = [];
  data: any;

  labelChannel = 'report.conversationAnalytics.channelsFilterLabel';
  analyticsMessagesData: AnalyticsMessagesData;

  allConversations: CardData[];
  conversationsPaid: CardData[];
  conversationFree: CardData[];
  conversationsCost: CardData[];
  totalConversationsCount = 0;
  totalFreeConversationsCount = 0;
  totalPaidConversationsCount = 0;
  totalPaidConversationsCost = '';

  allConversationsChart: ChartData[];
  freeConversationsChart: ChartData[];
  paidConversationsChart: ChartData[];
  costConversationsChart: ChartData[];

  isDataAnalyticsExists = true;

  colorScheme: Color = {
    name: 'custom',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA']
  };

  breadcrumb = [
    { href: '/report', label: 'report.breadcrumb.reports' },
    { label: 'report.breadcrumb.bcConversationAnalytics' },
  ];

  constructor(
    injector: Injector,
    private insightsService: InsightsService,
    private channelService: ChannelService
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.setupForms();
    this.getListChannel();
  }

  private setupForms(): void {
    const today = new Date(new Date().toISOString().slice(0, -1) + 'Z');
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setUTCDate(today.getUTCDate() - 30);

    this.formGroup = this.formBuilder.group({
      channel: [null],
      dateRange: this.formBuilder.group({
        start: [thirtyDaysAgo],
        end: [new Date(new Date().toISOString().slice(0, -1) + 'Z')]
      }),
      granularity: 'DAILY',
      fields: 'conversation_analytics',
      filteringParameter: this.formBuilder.group({
        dimensions: [['CONVERSATION_CATEGORY', 'CONVERSATION_TYPE', 'CONVERSATION_DIRECTION']]
      })
    });

    let previousValue = this.formGroup.value;

    this.formGroup.valueChanges
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(currentValue => {
        if (JSON.stringify(previousValue) !== JSON.stringify(currentValue)) {
          this.getAllAnalyticsData();
          previousValue = currentValue;
        }
      });
  }


  private getListChannel(): void {
    this.channelService.getList().subscribe({
      next: (channels) => {
        this.listChannel = channels.filter(c => c.type === ChannelTypeEnum.CLOUD_API);
        if (this.listChannel.length) {
          const defaultChannel = this.listChannel[0];
          this.labelChannel = defaultChannel.name;
          this.formGroup.get('channel')?.setValue(defaultChannel, { emitEvent: false });
          this.getAllAnalyticsData();
        }
      },
      error: (err) => console.error('Error fetching channels:', err)
    });
  }

  private getAllAnalyticsData(): void {
    this.loadingContent = true;

    const { channel, dateRange, granularity, fields, filteringParameter } = this.formGroup.getRawValue();

    this.analyticsMessagesData = {
      start: Math.floor(dateRange.start.getTime() / 1000),
      end: Math.floor(dateRange.end.getTime() / 1000),
      granularity,
      fields,
      filteringParameter
    };

    this.insightsService.getInsightsMessages(channel._id, this.analyticsMessagesData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: data => {
          this.isDataAnalyticsExists = !!data.id && !!data.conversation_analytics;
          this.data = data?.conversation_analytics?.data.flatMap((item: { data_points: ConversationDataPoint[] }) => item.data_points);
          this.filterAnalyticsData(this.data);
          this.loadingContent = false;
        },
        complete: () => this.loadingContent = false,
        error: () => this.loadingContent = false
      });
  }

  private filterAnalyticsData(data: ConversationDataPoint[]): ConversationDataPoint[] {
    const isFree = (type: ConversationTypeEnum) => [ConversationTypeEnum.FREE_ENTRY, ConversationTypeEnum.FREE_TIER].includes(type);
    const isPaid = (type: ConversationTypeEnum) => type === ConversationTypeEnum.REGULAR;

    this.totalConversationsCount = this.sumConversations(data);
    this.totalFreeConversationsCount = this.sumConversations(data.filter(p => isFree(p.conversation_type)));
    this.totalPaidConversationsCount = this.sumConversations(data.filter(p => isPaid(p.conversation_type)));
    this.totalPaidConversationsCost = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(data.filter(p => isPaid(p.conversation_type)).reduce((sum, p) => sum + p.cost, 0));

    this.allConversations = this.buildCardsData(data, 'conversation_category', CATEGORY_TRANSLATIONS);
    this.conversationsPaid = this.buildCardsData(data.filter(p => isPaid(p.conversation_type)), 'conversation_category', CATEGORY_TRANSLATIONS);
    this.conversationFree = this.buildCardsData(data.filter(p => isFree(p.conversation_type)), 'conversation_type', TYPE_TRANSLATIONS).filter(item => item.label !== 'Regular');
    this.conversationsCost = this.buildCardCostData(data, 'conversation_category', CATEGORY_TRANSLATIONS);

    const allConversationsChartRecord = this.buildChartData(data, 'conversation_category', CATEGORY_TRANSLATIONS).reduce((acc, item) => {
      acc[item.name] = item;
      return acc;
    }, {} as Record<string, { name: string; series: { name: string; value: number }[] }>);

    this.allConversationsChart = Object.values(allConversationsChartRecord);

    this.freeConversationsChart = this.buildChartData(data.filter(p => isFree(p.conversation_type)), 'conversation_type', TYPE_TRANSLATIONS).filter(item => item.name !== 'Regular');
    this.paidConversationsChart = this.buildChartData(data.filter(p => isPaid(p.conversation_type)), 'conversation_category', CATEGORY_TRANSLATIONS);
    this.costConversationsChart = this.buildChartCostData(data.filter(p => isPaid(p.conversation_type)), 'conversation_category', CATEGORY_TRANSLATIONS);

    return data;
  }

  private sumConversations(data: ConversationDataPoint[]): number {
    return data.reduce((sum, { conversation }) => sum + (conversation ?? 0), 0);
  }

  private buildCardsData(data: ConversationDataPoint[], key: keyof ConversationDataPoint, translations: Record<string, string>) {
    const dataCounts: Record<string, number> = {};

    for (const point of data) {
      const rawKey = point[key];
      const translatedKey = this.t(translations[rawKey] ?? String(rawKey));
      dataCounts[translatedKey] = (dataCounts[translatedKey] ?? 0) + point.conversation;
    }

    Object.values(translations).forEach((translatedKey: string) => {
      if (!(translatedKey in dataCounts)) {
        dataCounts[translatedKey] = 0;
      }
    });

    return this.mapToCardIData(dataCounts);
  }

  private buildCardCostData(data: ConversationDataPoint[], key: keyof ConversationDataPoint, translations: Record<string, string>): CardData[] {
    const costData: Record<string, number> = {};

    for (const point of data) {
      const rawKey = point[key];
      const translatedKey = this.t(translations[rawKey] ?? String(rawKey));
      costData[translatedKey] = (costData[translatedKey] ?? 0) + point.cost;
    }

    Object.values(translations).forEach((translatedKey: string) => {
      if (!(translatedKey in costData)) {
        costData[translatedKey] = 0;
      }
    });

    return Object.entries(costData).map(([label, value]) => ({
      label,
      value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value),
      colorClass: this.colorMap[label] ?? 'border-gray-400'
    }));
  }

  private buildChartData(data: ConversationDataPoint[], key: keyof ConversationDataPoint, translations: Record<string, string>) {
    const allDatesSet = new Set<number>();
    const allKeysSet = new Set<string>();

    const grouped = new Map<string, Map<string, number>>();

    for (const point of data) {
      const rawKey = point[key];
      const translatedKey = this.t(translations[rawKey] ?? String(rawKey));
      const formattedDate = DateUtil.toFormart(new Date(point.start * 1000), 'dd/MM', this.getTimezoneCompanyCurrentUser())

      allDatesSet.add(point.start);
      allKeysSet.add(translatedKey);

      if (!grouped.has(translatedKey)) {
        grouped.set(translatedKey, new Map());
      }

      const seriesMap = grouped.get(translatedKey)!;
      const existing = seriesMap.get(formattedDate) ?? 0;
      seriesMap.set(formattedDate, existing + point.conversation);
    }

    const allDatesSorted = Array.from(allDatesSet)
      .sort((a, b) => a - b)
      .map(timestamp => ({
        key: DateUtil.toFormart(new Date(timestamp * 1000), 'dd/MM', this.getTimezoneCompanyCurrentUser()),
      }));

    const result = Array.from(allKeysSet).map(seriesName => {
      const seriesMap = grouped.get(seriesName) ?? new Map();

      return {
        name: seriesName,
        series: allDatesSorted.map(date => {
          const value = seriesMap.get(date.key) ?? 0;
          return { name: date.key, value };
        }),
      };
    });

    return result;
  }

  private buildChartCostData(data: ConversationDataPoint[], key: keyof ConversationDataPoint, translations: Record<string, string>): ChartData[] {
    const allDatesSet = new Set<number>();
    const allKeysSet = new Set<string>();

    const conversationMap = new Map<string, Map<string, number>>();
    const costMap = new Map<string, Map<string, number>>();

    for (const point of data) {
      const rawKey = point[key];
      const translatedKey = this.t(translations[rawKey] ?? String(rawKey));
      const formattedDate = DateUtil.toFormart(new Date(point.start * 1000), 'dd/MM', this.getTimezoneCompanyCurrentUser());

      allDatesSet.add(point.start);
      allKeysSet.add(translatedKey);

      if (!conversationMap.has(translatedKey)) {
        conversationMap.set(translatedKey, new Map());
      }
      if (!costMap.has(translatedKey)) {
        costMap.set(translatedKey, new Map());
      }

      const convSeries = conversationMap.get(translatedKey)!;
      const costSeries = costMap.get(translatedKey)!;

      convSeries.set(formattedDate, point.conversation);
      costSeries.set(formattedDate, point.cost);
    }

    const allDatesSorted = Array.from(allDatesSet).sort((a, b) => a - b).map(timestamp => ({
      key: DateUtil.toFormart(new Date(timestamp * 1000), 'dd/MM', this.getTimezoneCompanyCurrentUser()),
    }));

    const formattedCurrency = (value: number) =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);

    const result = Array.from(allKeysSet).map(seriesName => {
      const convSeries = conversationMap.get(seriesName) ?? new Map();
      const costSeries = costMap.get(seriesName) ?? new Map();

      return {
        name: seriesName,
        value: convSeries.get(allDatesSorted[0]?.key) ?? 0,
        series: allDatesSorted.map(date => ({
          name: `${date.key} - ${formattedCurrency(costSeries.get(date.key) ?? 0)}`,
          value: convSeries.get(date.key) ?? 0,
        }))
      };
    });
    return result;
  }

  private mapToCardIData(source: Record<string, number>) {
    if (!source) return [];
    return Object.entries(source).map(([label, value]) => ({
      label,
      value,
      colorClass: this.colorMap[label] ?? 'border-gray-400'
    }));
  }

  searchByChannel(channel: Channel | null): void {
    this.labelChannel = channel?.name ?? 'report.conversationAnalytics.channelsFilterLabel';
    this.formGroup.get('channel')?.setValue(channel);
  }

  openPicker(): void {
    if (this.picker) {
      this.isPicker = true;
      setTimeout(() => this.picker.open(), 200);
    } else {
      console.error('Picker is not initialized');
    }
  }

  onPeriodChange(event: any): void {
    const days = Number(event.value);
    if (!isNaN(days)) {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);

      this.formGroup.get('dateRange')?.patchValue({ start, end });
      this.picker?.close();
    }
  }
}
